import { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/cn';

import { Tooltip } from './tooltip';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Ellipsis with a tooltip only when the text is actually cut off.
 *
 * A tooltip that repeats fully visible text is noise, and on a table of two hundred
 * rows it is noise that follows the pointer everywhere.
 */
export const TruncatedText = ({
  text,
  lines = 1,
  popoverPortal = 'body',
  className,
}: {
  text: string;
  lines?: number;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => {
      setTruncated(
        lines === 1
          ? node.scrollWidth > node.clientWidth
          : node.scrollHeight > node.clientHeight,
      );
    };

    measure();
    // Re-measured on resize: a sidebar collapsing or a column being dragged changes
    // the answer, and a stale one leaves text cut off with no way to read it.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [lines, text]);

  const content = (
    <span
      ref={ref}
      className={cn(
        'block min-w-0',
        lines === 1 ? 'truncate' : 'overflow-hidden',
        className,
      )}
      style={
        lines > 1
          ? { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical' }
          : undefined
      }
    >
      {text}
    </span>
  );

  if (!truncated) return content;

  return (
    <Tooltip content={text} popoverPortal={popoverPortal}>
      {content}
    </Tooltip>
  );
};
